/* eslint-disable no-else-return */

import { Dispatch, FC, ReactNode, SetStateAction, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';
import { Badge, Button, Checkbox, Dropdown, Menu, Space } from 'antd';
import { CiFilter } from 'react-icons/ci';
import type { RootState } from 'store';
import type { AppTableFilter, AppTableInitialState, AppTableSlice } from './store';
import styles from './filterDropdown.module.less';

interface FilterDropdownOptionItem {
  label: string;
  value: string | number | boolean;
  key?: string;
  only?: boolean;
  resetUnchecked?: boolean;
  single?: boolean;
}

interface FilterDropdownOptionDivider {
  type: 'divider';
}

interface FilterDropdownOptionGroup {
  type: 'group';
  key: string;
  title: string;
  children: FilterDropdownOptionItem[];
  single?: boolean;
  toggleAll?: boolean;
}

type FilterDropdownOption = FilterDropdownOptionItem | FilterDropdownOptionDivider | FilterDropdownOptionGroup;

type MenuMultipleValue = Record<string, AppTableFilter | AppTableFilter[]>;

type CheckFilterHasOption = (
  option: FilterDropdownOptionItem,
  objFilter?: Record<string, AppTableFilter | AppTableFilter[]>,
) => boolean;

const getCheckboxOptions = (options: FilterDropdownOption[]): FilterDropdownOptionItem[] => {
  return options.reduce<FilterDropdownOptionItem[]>((acc, option) => {
    if ('type' in option) {
      if (option.type === 'group') {
        return [...acc, ...getCheckboxOptions(option.children)];
      }

      return acc;
    }

    return [...acc, option];
  }, []);
};

interface MenuItemClearFilterProps {
  storeSlice: FilterDropdownProps['storeSlice'];
  filterKey: FilterDropdownProps['filterKey'];
  isArray?: boolean;
}

const MenuItemClearFilter: FC<MenuItemClearFilterProps> = ({ storeSlice, filterKey = '', isArray }) => {
  const dispatch = useDispatch();

  return (
    <Menu.Item
      key="clear-filters"
      onClick={() =>
        dispatch(
          storeSlice.actions.setFilter({
            [filterKey]: isArray ? [] : undefined,
          }),
        )
      }
    >
      Clear filters
    </Menu.Item>
  );
};

type GroupCheckState = 'all' | 'some' | 'none';

interface MenuItemProps {
  itemKey?: string;
  option: FilterDropdownOption;
  checkbox?: boolean;
  checkFilterHasOption: CheckFilterHasOption;
  onClick?: (option: FilterDropdownOptionItem) => void;
  onToggleGroup?: (group: FilterDropdownOptionGroup) => void;
  getGroupCheckState?: (group: FilterDropdownOptionGroup) => GroupCheckState;
  onOptionRender: FilterDropdownProps['onOptionRender'];
}

const MenuItem: FC<MenuItemProps> = ({
 itemKey,
 option,
 checkbox,
 checkFilterHasOption,
 onClick,
 onToggleGroup,
 getGroupCheckState,
 onOptionRender,
}) => {
  if ('type' in option) {
    if (option.type === 'divider') {
      return <Menu.Divider />;
    }

    const groupState = option.toggleAll && onToggleGroup && getGroupCheckState ? getGroupCheckState(option) : undefined;

    const groupTitle =
      groupState !== undefined ? (
        <button
          type="button"
          className={styles.groupToggle}
          onClick={(event) => {
            event.stopPropagation();
            onToggleGroup?.(option);
          }}
        >
          <Checkbox checked={groupState === 'all'} indeterminate={groupState === 'some'} />
          <span>{option.title}</span>
        </button>
      ) : (
        option.title
      );

    return (
      <Menu.ItemGroup key={option.key} title={groupTitle}>
        {option.children.map((item) => (
          <MenuItem
            key={itemKey}
            itemKey={itemKey}
            option={option.single ? { ...item, single: true } : item}
            checkbox={checkbox}
            checkFilterHasOption={checkFilterHasOption}
            onClick={onClick}
            onOptionRender={onOptionRender}
          />
        ))}
      </Menu.ItemGroup>
    );
  }

  return (
    <Menu.Item key={itemKey} onClick={() => onClick?.(option)}>
      {checkbox && <Checkbox checked={checkFilterHasOption(option)} />}{' '}
      {onOptionRender ? onOptionRender(option.label, option) : option.label}
    </Menu.Item>
  );
};

interface DropdownMultipleProps {
  open?: boolean;
  value: MenuMultipleValue;
  setValue: Dispatch<SetStateAction<MenuMultipleValue>>;
  storeSlice: FilterDropdownProps['storeSlice'];
  options: FilterDropdownProps['options'];
  filterKey: FilterDropdownProps['filterKey'];
  showReset: FilterDropdownProps['showReset'];
  checkFilterHasOption: CheckFilterHasOption;
  onOptionRender: FilterDropdownProps['onOptionRender'];
  onReset: FilterDropdownProps['onReset'];
  onCloseDropdown: () => void;
}

const DropdownMultiple: FC<DropdownMultipleProps> = ({
 open,
 value,
 setValue,
 storeSlice,
 options,
 filterKey = '',
 showReset,
 checkFilterHasOption,
 children,
 onOptionRender,
 onReset,
 onCloseDropdown,
}) => {
  const dispatch = useDispatch();

  const setFilterValue = (objFilter: Record<string, AppTableFilter | AppTableFilter[]>, option: FilterDropdownOptionItem) => {
    const newObject = { ...objFilter };

    const filterTarget = newObject[option.key ?? filterKey];

    if (option.single) {
      newObject[option.key ?? filterKey] = { value: option.value, label: option.label };
    } else if (Array.isArray(filterTarget)) {
      newObject[option.key ?? filterKey] = option.only
        ? [{ value: option.value, label: option.label }]
        : [...filterTarget, { value: option.value, label: option.label }];
    } else {
      newObject[option.key ?? filterKey] = { value: option.value, label: option.label };
    }

    return newObject;
  };

  const removeFilterValue = (objFilter: Record<string, AppTableFilter | AppTableFilter[]>, option: FilterDropdownOptionItem) => {
    const newObject: any = { ...objFilter };

    const filterTarget = newObject[option.key ?? filterKey];

    if (!option.single && Array.isArray(filterTarget) && !option.resetUnchecked) {
      newObject[option.key ?? filterKey] = filterTarget.filter((filter) => filter.value !== option.value);
    } else {
      newObject[option.key ?? filterKey] = undefined;
    }

    return newObject;
  };

  const onClickHandler = (option: FilterDropdownOptionItem) => {
    const checked = checkFilterHasOption(option);
    setValue(checked ? removeFilterValue(value, option) : setFilterValue(value, option));
  };

  const getGroupCheckState = (group: FilterDropdownOptionGroup): GroupCheckState => {
    const checkedCount = group.children.filter((child) => checkFilterHasOption(child, value)).length;

    if (checkedCount === 0) {
      return 'none';
    }

    return checkedCount === group.children.length ? 'all' : 'some';
  };

  const onToggleGroup = (group: FilterDropdownOptionGroup) => {
    const shouldCheckAll = getGroupCheckState(group) !== 'all';

    const nextValue = group.children.reduce((acc, child) => {
      const isChecked = checkFilterHasOption(child, acc);

      if (shouldCheckAll && !isChecked) {
        return setFilterValue(acc, child);
      }

      if (!shouldCheckAll && isChecked) {
        return removeFilterValue(acc, child);
      }

      return acc;
    }, value);

    setValue(nextValue);
  };

  const menu = (
    <Menu style={{ zIndex: 100 }} selectable={false}>
      {options.map((option, index) => (
        <MenuItem
          key={
            'type' in option
              ? `${option.type === 'group' ? option.key : `divider-${index}`}`
              : `${option.key ?? filterKey}-${option.value}`
          }
          option={option}
          checkbox
          checkFilterHasOption={checkFilterHasOption}
          onToggleGroup={onToggleGroup}
          getGroupCheckState={getGroupCheckState}
          onOptionRender={onOptionRender}
          onClick={onClickHandler}
        />
      ))}

      {showReset ? (
        <Menu.Item key="reset-filters">
          <Space>
            <Button
              size="small"
              onClick={() => {
                if (filterKey) {
                  dispatch(storeSlice.actions.resetFilters({ key: filterKey }));
                } else {
                  const uniqueKeys = Array.from(new Set(getCheckboxOptions(options).map((opt) => opt.key ?? filterKey)));

                  uniqueKeys.forEach((key) => {
                    if (key) {
                      dispatch(storeSlice.actions.resetFilters({ key }));
                    }
                  });
                }

                onReset?.();

                onCloseDropdown();
              }}
            >
              Reset
            </Button>

            <Button
              onClick={() => {
                const resetOptions = getCheckboxOptions(options).filter(
                  (option): option is FilterDropdownOptionItem =>
                    option.resetUnchecked === true && !!option.key && !value[option.key],
                );

                if (resetOptions.length) {
                  dispatch(storeSlice.actions.setIgnoreFetch(true));
                }

                dispatch(storeSlice.actions.setFilter(value));

                if (resetOptions.length) {
                  resetOptions.forEach((option) => dispatch(storeSlice.actions.resetFilters({ key: option.key ?? '' })));
                  dispatch(storeSlice.actions.setIgnoreFetch(false));
                  dispatch(
                    storeSlice.actions.fetch({
                      params: {
                        'page[number]': 1,
                      },
                    }),
                  );
                }

                onCloseDropdown();
              }}
              type="primary"
              size="small"
            >
              Ok
            </Button>
          </Space>
        </Menu.Item>
      ) : (
        <MenuItemClearFilter storeSlice={storeSlice} filterKey={filterKey} isArray={Array.isArray(value[filterKey])} />
      )}
    </Menu>
  );

  return (
    <Dropdown visible={open} disabled={!options.length} overlay={menu} placement="bottomRight">
      {children}
    </Dropdown>
  );
};

interface DropdownSimpleProps {
  open?: boolean;
  store: AppTableInitialState;
  storeSlice: FilterDropdownProps['storeSlice'];
  options: FilterDropdownProps['options'];
  filterKey: FilterDropdownProps['filterKey'];
  checkFilterHasOption: CheckFilterHasOption;
  onOptionRender: FilterDropdownProps['onOptionRender'];
}

const DropdownSimple: FC<DropdownSimpleProps> = ({
 open,
 store,
 storeSlice,
 options,
 filterKey = '',
 checkFilterHasOption,
 children,
 onOptionRender,
}) => {
  const dispatch = useDispatch();

  const onClickHandler = (option: FilterDropdownOptionItem) => {
    const currentValue = store.params.filters[option.key ?? filterKey];

    const newOption = {
      value: option.value,
      label: option.label,
    };

    dispatch(
      storeSlice.actions.setFilter({
        [option.key ?? filterKey]: Array.isArray(currentValue) ? [newOption] : newOption,
      }),
    );
  };

  const menu = (
    <Menu
      style={{ zIndex: 100 }}
      selectedKeys={getCheckboxOptions(options)
        .filter((option) => checkFilterHasOption(option, store.params.filters))
        .map((option) => option.value.toString())}
    >
      {options.map((option, index) => (
        <MenuItem
          key={'type' in option ? `${option.type === 'group' ? option.key : `divider-${index}`}` : option.value.toString()}
          itemKey={'type' in option ? `${option.type === 'group' ? option.key : `divider-${index}`}` : option.value.toString()}
          option={option}
          checkFilterHasOption={checkFilterHasOption}
          onClick={onClickHandler}
          onOptionRender={onOptionRender}
        />
      ))}

      <MenuItemClearFilter
        storeSlice={storeSlice}
        filterKey={filterKey}
        isArray={Array.isArray(store.params.filters[filterKey])}
      />
    </Menu>
  );

  return (
    <Dropdown visible={open} disabled={!options.length} overlay={menu}>
      {children}
    </Dropdown>
  );
};

export interface FilterDropdownProps {
  storeSlice: AppTableSlice;
  icon?: ReactNode;
  filterKey?: string;
  label?: string;
  options: FilterDropdownOption[];
  multiple?: boolean;
  showReset?: boolean;
  showCount?: boolean;
  onOptionRender?: (label: string, option: any) => ReactNode;
  onReset?: () => void;
}

const FilterDropdown: FC<FilterDropdownProps> = ({
  storeSlice,
  icon,
  filterKey = '',
  label = 'Filters',
  options,
  multiple = false,
  showReset = true,
  showCount = true,
  onOptionRender,
  onReset,
}) => {
  const store = useSelector((globalStore: RootState) => globalStore[storeSlice.name] as AppTableInitialState);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<MenuMultipleValue>({});

  const checkFilterHasOption: CheckFilterHasOption = (option, objFilter = current) => {
    if ('type' in option) {
      return false;
    }

    const filterTarget = objFilter[option.key ?? filterKey];

    if (Array.isArray(filterTarget)) {
      return !!filterTarget.find((x) => String(x.value) === String(option.value));
    } else {
      return String(filterTarget?.value) === String(option.value);
    }
  };

  const getBadgeCount = () => {
    return getCheckboxOptions(options).filter((option) => checkFilterHasOption(option, store.params.filters)).length;
  };

  const onOpenDropdownHandler = () => {
    setCurrent(cloneDeep(store.params.filters));

    setOpen(true);
  };

  const button = (
    <Button
      className="btn-outline-primary ant-btn-icon-center"
      size="large"
      type="default"
      icon={<span className={styles.icon}>{icon ?? <CiFilter strokeWidth={1} />}</span>}
      block
      onClick={onOpenDropdownHandler}
    >
      <span>{label}</span>

      {showCount && <Badge count={getBadgeCount()} className="ant-badge-color-dark-blue ml-m" />}
    </Button>
  );

  return (
    <>
      {open && <div onClick={() => setOpen(false)} className={styles.overlay} />}

      {multiple ? (
        <DropdownMultiple
          open={open}
          value={current}
          setValue={setCurrent}
          storeSlice={storeSlice}
          options={options}
          filterKey={filterKey}
          showReset={showReset}
          checkFilterHasOption={checkFilterHasOption}
          onOptionRender={onOptionRender}
          onReset={onReset}
          onCloseDropdown={() => setOpen(false)}
        >
          {button}
        </DropdownMultiple>
      ) : (
        <DropdownSimple
          open={open}
          store={store}
          storeSlice={storeSlice}
          options={options}
          filterKey={filterKey}
          checkFilterHasOption={checkFilterHasOption}
          onOptionRender={onOptionRender}
        >
          {button}
        </DropdownSimple>
      )}
    </>
  );
};

export default FilterDropdown;
